import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { CountrySelect } from "@/components/CountrySelect";
import { WhatsappInput } from "@/components/WhatsappInput";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/Navbar";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useDarkMode } from "@/context/DarkModeContext";
import StatusBadge from "@/components/StatusBadge";
import ChatWidget from "@/components/ChatWidget";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import VideoCall from "@/components/VideoCall";
import IncomingCall from "@/components/IncomingCall";
import ScreeningQuiz from "@/components/ScreeningQuiz";
import AudioPlayer from "@/components/AudioPlayer";
import { Avatar } from "@/components/Avatar";
import ImageLightbox from "@/components/ImageLightbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  FileText, Plus, Briefcase, Clock, CheckCircle2, XCircle, CalendarDays, Video,
  ScrollText, User, Loader2, Sparkles, Home, MapPin, Search, ArrowRight, Star, Menu, MessageCircle, Camera, LogOut, Sun, Moon, Trash2, Gauge, Monitor,
} from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const STEPS = [
  { key: "pending", label: "Reçue / En attente", Icon: Clock },
  { key: "accepted", label: "Acceptée", Icon: CheckCircle2 },
];

const fmtDate = (d) => { try { return new Date(d + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }); } catch { return d; } };

export default function CandidateDashboard() {
  const { user, checkAuth, logout } = useAuth();
  const navigate = useNavigate();
  const { dark, toggle } = useDarkMode();
  const [apps, setApps] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [profile, setProfile] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const section = searchParams.get("section") || "home";
  const setSection = useCallback((key, opts) => setSearchParams({ section: key }, opts), [setSearchParams]);
  const [mobileNav, setMobileNav] = useState(false);
  const [call, setCall] = useState(null);
  const [incoming, setIncoming] = useState(null);
  const [reminder, setReminder] = useState(null);
  const didAutoNav = useRef(!!searchParams.get("section"));
  const alerted = useRef(new Set());
  const [chatMeta, setChatMeta] = useState({ active: false, unread: 0 });

  useEffect(() => {
    const load = () => api.get("/chat/unread").then(({ data }) => setChatMeta(data)).catch(() => {});
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const loadAll = useCallback(() => {
    api.get("/applications/me").then(({ data }) => setApps(data)).catch(() => {});
    api.get("/jobs").then(({ data }) => setJobs(data)).catch(() => {});
    api.get("/interviews/me").then(({ data }) => setInterviews(data)).catch(() => {});
    api.get("/contracts/me").then(({ data }) => setContracts(data)).catch(() => {});
    api.get("/notifications").then(({ data }) => setNotifs(data.items || [])).catch(() => {});
    api.get("/profile").then(({ data }) => {
      setProfile(data);
      if (!data.profile_completed && !didAutoNav.current) { setSection("profile", { replace: true }); didAutoNav.current = true; }
    }).catch(() => {});
  }, [setSection]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    const poll = () => api.get("/calls/incoming").then(({ data }) => {
      if (data && data.id) setIncoming((prev) => (prev && prev.id === data.id ? prev : data));
      else setIncoming(null);
    }).catch(() => {});
    poll();
    const t = setInterval(poll, 4000);
    return () => clearInterval(t);
  }, []);

  const acceptCall = async () => {
    try { await api.put(`/calls/${incoming.id}/status`, { status: "accepted" }); } catch (e) { console.warn("accept call status failed", e); }
    setCall({ room: incoming.room, audioOnly: incoming.mode === "audio" });
    setIncoming(null);
  };
  const declineCall = async () => {
    try { await api.put(`/calls/${incoming.id}/status`, { status: "declined" }); } catch (e) { console.warn("decline call status failed", e); }
    setIncoming(null);
  };

  useEffect(() => {
    const check = () => {
      const now = Date.now();
      for (const i of interviews) {
        if (!i.date || !i.time) continue;
        const start = new Date(`${i.date}T${i.time}`).getTime();
        const diff = start - now;
        if (diff > 0 && diff <= 5 * 60 * 1000) {
          setReminder(i);
          if (!alerted.current.has(i.id)) {
            alerted.current.add(i.id);
            toast.info(`Votre entretien « ${i.title} » commence dans ${Math.ceil(diff / 60000)} min`, { duration: 10000 });
          }
          return;
        }
      }
      setReminder(null);
    };
    check();
    const t = setInterval(check, 30000);
    return () => clearInterval(t);
  }, [interviews]);

  const unreadByType = (t) => notifs.filter((n) => !n.read && n.type === t).length;

  const NAV = [
    { key: "home", label: "Accueil", Icon: Home },
    { key: "applications", label: "Mes postulations", Icon: FileText, badge: unreadByType("status"), count: apps.length },
    { key: "interviews", label: "Mes entretiens", Icon: CalendarDays, badge: unreadByType("interview"), count: interviews.length },
    { key: "contracts", label: "Contrats obtenus", Icon: ScrollText, count: contracts.length },
    { key: "profile", label: "Mon profil", Icon: User, badge: profile && !profile.profile_completed ? "!" : 0 },
    ...(chatMeta.active ? [{ key: "messages", label: "Messagerie recruteur", Icon: MessageCircle, badge: chatMeta.unread, action: "chat" }] : []),
  ];

  const selectSection = (key) => { setSection(key); setMobileNav(false); };
  const navBlock = (
    <>
      <nav className="space-y-1.5">
        {NAV.map((n) => (
          <button
            key={n.key}
            onClick={() => n.action === "chat" ? (window.dispatchEvent(new CustomEvent("open-support-chat")), setMobileNav(false)) : selectSection(n.key)}
            data-testid={`sidebar-${n.key}`}
            className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${section === n.key ? "bg-primary text-primary-foreground" : "hover:bg-secondary text-muted-foreground hover:text-foreground"}`}
          >
            <n.Icon className="h-4 w-4" />
            <span className="flex-1 text-left">{n.label}</span>
            {typeof n.count === "number" && n.count > 0 && (
              <span className={`text-xs ${section === n.key ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{n.count}</span>
            )}
            {n.badge ? (
              <span data-testid={`sidebar-badge-${n.key}`} className="h-5 min-w-5 px-1 rounded-full bg-destructive text-white text-[10px] font-bold flex items-center justify-center">{n.badge}</span>
            ) : null}
          </button>
        ))}
      </nav>
    </>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {incoming && <IncomingCall call={incoming} onAccept={acceptCall} onDecline={declineCall} />}
      {call && <VideoCall room={call.room} audioOnly={call.audioOnly} title="Entretien" onClose={() => setCall(null)} />}
      <aside className="w-64 shrink-0 border-r border-border bg-card hidden lg:flex flex-col" data-testid="candidate-sidebar">
        <div className="h-16 flex items-center px-5 border-b border-border">
          <button onClick={() => navigate("/")} className="flex items-center gap-2.5" data-testid="candidate-logo">
            <img src="/logo.png" alt="Talent Vortex" className="h-9 w-9 rounded-lg object-contain bg-white p-0.5" />
            <span className="font-display text-lg font-semibold">Talent Vortex</span>
          </button>
        </div>
        <div className="flex-1 p-3 overflow-y-auto">{navBlock}</div>
        <div className="p-3 border-t border-border space-y-2">
          <p className="text-xs text-muted-foreground truncate px-1">{user?.email}</p>
          <Button variant="outline" className="w-full rounded-lg" onClick={() => { logout(); navigate("/"); }} data-testid="candidate-logout-btn"><LogOut className="h-4 w-4 mr-2" /> Déconnexion</Button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <div className="hidden lg:flex items-center justify-end gap-2 px-8 h-16 border-b border-border sticky top-0 bg-background/80 backdrop-blur-md z-20" data-testid="candidate-header">
          <LanguageSwitcher />
          <NotificationBell />
          <button onClick={toggle} data-testid="candidate-dark-toggle" aria-label="Basculer le thème" className="h-9 w-9 rounded-full border border-border flex items-center justify-center hover:bg-secondary transition-colors">
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => { logout(); navigate("/"); }} data-testid="candidate-header-logout"><LogOut className="h-4 w-4 mr-2" /> Déconnexion</Button>
        </div>
        <div className="lg:hidden flex items-center gap-3 p-3 border-b border-border">
          <Sheet open={mobileNav} onOpenChange={setMobileNav}>
            <SheetTrigger asChild>
              <button className="h-10 w-10 rounded-lg border border-border flex items-center justify-center hover:bg-secondary transition-colors" data-testid="candidate-mobile-menu-btn" aria-label="Menu"><Menu className="h-5 w-5" /></button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72 flex flex-col">
              <SheetHeader className="p-4 border-b border-border text-left"><SheetTitle className="flex items-center gap-2"><img src="/logo.png" alt="Talent Vortex" className="h-8 w-8 rounded-lg object-contain bg-white p-0.5" /> Talent Vortex</SheetTitle></SheetHeader>
              <div className="flex-1 p-3 overflow-y-auto">{navBlock}</div>
              <div className="p-3 border-t border-border"><Button variant="outline" className="w-full rounded-lg" onClick={() => { logout(); navigate("/"); }} data-testid="candidate-mobile-logout-btn"><LogOut className="h-4 w-4 mr-2" /> Déconnexion</Button></div>
            </SheetContent>
          </Sheet>
          <button onClick={() => navigate("/")} className="font-display text-lg font-semibold flex items-center gap-2" data-testid="candidate-mobile-logo"><img src="/logo.png" alt="Talent Vortex" className="h-7 w-7 rounded-md object-contain bg-white p-0.5" /> Talent Vortex</button>
          <div className="ml-auto flex items-center gap-2"><LanguageSwitcher /><NotificationBell /></div>
        </div>
        <div className="p-6 md:p-8 max-w-6xl">
          {reminder && (
            <div className="rounded-2xl border border-primary bg-primary/10 p-4 mb-6 flex items-center justify-between gap-3" data-testid="interview-reminder-banner">
              <div className="flex items-center gap-3">
                <CalendarDays className="h-5 w-5 text-primary shrink-0" />
                <div>
                  <p className="font-medium">Entretien imminent : {reminder.title}</p>
                  <p className="text-sm text-muted-foreground">Aujourd'hui à {reminder.time}</p>
                </div>
              </div>
              <Button size="sm" className="rounded-full" onClick={() => setCall({ room: `recrutai-itw-${reminder.id}`, audioOnly: false })} data-testid="reminder-join-btn"><Video className="h-4 w-4 mr-1.5" /> Rejoindre</Button>
            </div>
          )}
          {profile && !profile.profile_completed && (
            <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5 mb-6 flex items-start gap-3" data-testid="profile-incomplete-banner">
              <Sparkles className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium">Complétez votre profil</p>
                <p className="text-sm text-muted-foreground">Renseignez vos infos et domaines d'expertise : l'IA vous proposera en priorité les offres de votre domaine.</p>
              </div>
              <Button size="sm" className="rounded-full" onClick={() => setSection("profile")} data-testid="complete-profile-btn">Compléter</Button>
            </div>
          )}

          {section === "home" && <JobsHome jobs={jobs} apps={apps} />}
          {section === "applications" && <Applications apps={apps} onBrowse={() => setSection("home")} onReload={loadAll} />}
          {section === "interviews" && <InterviewsView interviews={interviews} onReload={loadAll} />}
          {section === "contracts" && <ContractsView contracts={contracts} />}
          {section === "profile" && <ProfileForm profile={profile} onSaved={() => { loadAll(); checkAuth(); }} />}
        </div>
      </main>
      <ChatWidget />
          </div>
  );
}

function RecruiterChatCard() {
  const [meta, setMeta] = useState({ active: false, unread: 0 });
  useEffect(() => {
    const load = () => api.get("/chat/unread").then(({ data }) => setMeta(data)).catch(() => {});
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);
  if (!meta.active) return null;
  return (
    <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5 flex items-center justify-between gap-4" data-testid="recruiter-chat-card">
      <div>
        <p className="font-display font-semibold">Messagerie recruteur</p>
        <p className="text-sm text-muted-foreground">{meta.unread > 0 ? `${meta.unread} nouveau(x) message(s) du recruteur` : "Discutez directement avec le recruteur."}</p>
      </div>
      <Button className="rounded-full shrink-0" onClick={() => window.dispatchEvent(new CustomEvent("open-support-chat"))} data-testid="open-recruiter-chat-btn">
        Ouvrir{meta.unread > 0 ? ` (${meta.unread})` : ""}
      </Button>
    </div>
  );
}

function JobsHome({ jobs, apps }) {
  const [q, setQ] = useState("");
  const statusByJob = {};
  for (const a of apps) statusByJob[a.job_id] = a.status;

  const filtered = jobs.filter(
    (j) =>
      j.title.toLowerCase().includes(q.toLowerCase()) ||
      j.company.toLowerCase().includes(q.toLowerCase()) ||
      j.location.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="candidate-home">
      <RecruiterChatCard />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="label-caps text-primary mb-1">Offres disponibles</p>
          <h2 className="font-display text-2xl font-semibold">{filtered.length} publication{filtered.length > 1 ? "s" : ""}</h2>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une offre..." className="pl-10 rounded-full" data-testid="home-job-search" />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-16 text-center text-muted-foreground" data-testid="no-jobs-home">
          <Briefcase className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
          Aucune offre disponible pour le moment.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((job, i) => {
            const status = statusByJob[job.id];
            return (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (i % 4) * 0.05 }}
                className="flex h-full flex-col rounded-2xl border border-border bg-card p-6 hover:border-primary transition-colors"
                data-testid={`home-job-${job.id}`}
              >
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className="text-xs font-semibold rounded-full bg-secondary px-3 py-1">{job.category}</span>
                  <span className="text-xs text-muted-foreground">{job.type}</span>
                  {job.match_percent ? (
                    <span className="text-xs font-bold text-primary bg-primary/10 rounded-full px-2.5 py-1 flex items-center gap-1" data-testid={`home-match-${job.id}`}>
                      <Star className="h-3 w-3 fill-primary" /> {job.match_percent}% compatible
                    </span>
                  ) : null}
                </div>
                <Link to={`/jobs/${job.id}`} className="hover:text-primary transition-colors" data-testid={`home-job-title-${job.id}`}>
                  <h3 className="font-display text-xl font-semibold mb-1">{job.title}</h3>
                </Link>
                <p className="text-sm font-medium text-muted-foreground mb-3">{job.company}</p>
                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-5">
                  <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {job.location}</span>
                  {job.salary && <span className="flex items-center gap-1.5"><Briefcase className="h-4 w-4" /> {job.salary}</span>}
                </div>
                <div className="mt-auto flex items-center justify-between gap-3">
                  {status ? (
                    <>
                      <StatusBadge status={status} />
                      <Button asChild variant="outline" size="sm" className="rounded-full" data-testid={`home-view-${job.id}`}>
                        <Link to={`/jobs/${job.id}`}>Voir</Link>
                      </Button>
                    </>
                  ) : (
                    <Button asChild size="sm" className="rounded-full ml-auto" data-testid={`home-apply-${job.id}`}>
                      <Link to={`/jobs/${job.id}#postuler`}>Postuler <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
                    </Button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Applications({ apps, onBrowse, onReload }) {
  const [quizApp, setQuizApp] = useState(null);
  const autoRef = useRef(false);
  useEffect(() => {
    if (!autoRef.current) {
      const pend = apps.find((a) => a.screening?.questions?.length && !a.screening?.completed);
      if (pend) { setQuizApp(pend); autoRef.current = true; }
    }
  }, [apps]);

  if (apps.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border p-16 text-center" data-testid="no-applications">
        <Briefcase className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
        <p className="text-muted-foreground mb-5">Vous n'avez pas encore postulé.</p>
        <Button onClick={onBrowse} className="rounded-full">Parcourir les offres</Button>
      </div>
    );
  }
  return (
    <div className="space-y-5">
      <h2 className="font-display text-2xl font-semibold">Mes postulations</h2>
      {apps.map((a, i) => (
        <motion.div key={a.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="rounded-2xl border border-border bg-card p-6" data-testid={`application-${a.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div>
              <h3 className="font-display text-xl font-semibold">{a.job_title}</h3>
              <p className="text-sm text-muted-foreground">Postulé le {new Date(a.created_at).toLocaleDateString("fr-FR")}</p>
            </div>
            <StatusBadge status={a.status} />
          </div>
          <div className="flex items-center gap-2 mb-5">
            {a.status === "rejected" ? (
              <div className="flex items-center gap-2 text-sm text-[hsl(var(--status-rejected))]"><XCircle className="h-5 w-5" /> Candidature non retenue</div>
            ) : (
              STEPS.map((s, idx) => {
                const active = s.key === "pending" || a.status === "accepted";
                const isCurrent = (a.status === "pending" && s.key === "pending") || (a.status === "accepted" && s.key === "accepted");
                return (
                  <div key={s.key} className="flex items-center gap-2">
                    <div className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium ${active ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"} ${isCurrent ? "ring-2 ring-primary/30" : ""}`}>
                      <s.Icon className="h-3.5 w-3.5" /> {s.label}
                    </div>
                    {idx < STEPS.length - 1 && <div className={`h-0.5 w-8 ${a.status === "accepted" ? "bg-primary" : "bg-border"}`} />}
                  </div>
                );
              })
            )}
          </div>
          {a.cover_note && <p className="text-sm text-muted-foreground mb-4 italic">"{a.cover_note}"</p>}
          {a.screening?.questions?.length > 0 && (
            a.screening.completed ? (
              <div className="flex items-center gap-2 text-sm text-primary mb-4" data-testid={`screening-completed-${a.id}`}><CheckCircle2 className="h-4 w-4" /> Examen de pré-qualification complété</div>
            ) : (
              <div className="rounded-xl bg-primary/5 border border-primary/20 p-4 mb-4 flex flex-wrap items-center justify-between gap-3" data-testid={`screening-pending-${a.id}`}>
                <div className="flex items-center gap-2 text-sm"><Sparkles className="h-4 w-4 text-primary" /> Un court examen vous attend pour finaliser cette candidature.</div>
                <Button size="sm" className="rounded-full" onClick={() => setQuizApp(a)} data-testid={`answer-screening-${a.id}`}>Répondre à l'examen</Button>
              </div>
            )
          )}
          {a.transcription && (
            <div className="rounded-lg bg-secondary/50 p-3 mb-4">
              <p className="text-xs font-semibold text-muted-foreground mb-1">Transcription de votre message vocal</p>
              <p className="text-sm">{a.transcription}</p>
            </div>
          )}
          <div className="flex flex-col gap-3">
            {a.voice_file_id && <AudioPlayer src={fileUrl(a.voice_file_id)} testId={`voice-player-${a.id}`} />}
            {a.cv_file_id && (
              <Button asChild variant="outline" size="sm" className="rounded-full w-fit" data-testid={`view-cv-${a.id}`}>
                <a href={fileUrl(a.cv_file_id)} target="_blank" rel="noreferrer"><FileText className="h-4 w-4 mr-2" /> Mon CV</a>
              </Button>
            )}
          </div>
        </motion.div>
      ))}
      <ScreeningQuiz app={quizApp} open={!!quizApp} onClose={() => setQuizApp(null)} onDone={() => onReload && onReload()} />
    </div>
  );
}

function InterviewsView({ interviews, onReload }) {
  const [openId, setOpenId] = useState(null);
  const removeItw = async (id) => {
    if (!window.confirm("Supprimer cet entretien de votre agenda ?")) return;
    try { await api.delete(`/interviews/me/${id}`); toast.success("Entretien supprimé"); setOpenId(null); onReload?.(); }
    catch { toast.error("Suppression impossible"); }
  };
  if (interviews.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border p-16 text-center text-muted-foreground" data-testid="no-interviews">
        <CalendarDays className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
        Aucun entretien planifié pour le moment.
      </div>
    );
  }
  const STATUS_LABEL = { scheduled: "Planifié", done: "Terminé", cancelled: "Annulé" };
  return (
    <div className="space-y-4" data-testid="candidate-interviews">
      <h2 className="font-display text-2xl font-semibold">Mes entretiens</h2>
      {interviews.map((i) => (
        <div key={i.id} className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`candidate-interview-${i.id}`}>
          <button className="w-full text-left p-5 flex items-center gap-4 hover:bg-secondary/40 transition-colors" onClick={() => setOpenId(openId === i.id ? null : i.id)} data-testid={`candidate-interview-toggle-${i.id}`}>
            <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <span className="font-mono text-sm font-bold">{i.time}</span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium truncate">{i.title}</p>
              <p className="text-xs text-muted-foreground capitalize">{fmtDate(i.date)}{i.location ? ` • ${i.location}` : ""}</p>
            </div>
            <ArrowRight className={`h-4 w-4 text-muted-foreground transition-transform ${openId === i.id ? "rotate-90" : ""}`} />
          </button>
          {openId === i.id && (
            <div className="px-5 pb-5 pt-1 border-t border-border space-y-3" data-testid={`interview-details-${i.id}`}>
              <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm pt-3">
                <div><dt className="text-muted-foreground">Intitulé</dt><dd className="font-medium">{i.title}</dd></div>
                <div><dt className="text-muted-foreground">Statut</dt><dd className="font-medium">{STATUS_LABEL[i.status] || i.status || "Planifié"}</dd></div>
                <div><dt className="text-muted-foreground">Date</dt><dd className="font-medium capitalize">{fmtDate(i.date)}</dd></div>
                <div><dt className="text-muted-foreground">Heure</dt><dd className="font-medium">{i.time}</dd></div>
                {i.location && <div className="sm:col-span-2"><dt className="text-muted-foreground">Lieu / Lien</dt><dd className="font-medium break-words">{i.location}</dd></div>}
                {i.notes && <div className="sm:col-span-2"><dt className="text-muted-foreground">Notes</dt><dd className="font-medium whitespace-pre-wrap">{i.notes}</dd></div>}
              </dl>
              <div className="flex justify-end pt-1">
                <Button size="sm" variant="outline" className="rounded-full text-destructive border-destructive/40 hover:bg-destructive/10" onClick={() => removeItw(i.id)} data-testid={`candidate-delete-interview-${i.id}`}>
                  <XCircle className="h-4 w-4 mr-1.5" /> Supprimer
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ContractsView({ contracts }) {
  const LABELS = { en_cours: "En cours", boucle: "Bouclé", resilie: "Résilié" };
  if (contracts.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border p-16 text-center text-muted-foreground" data-testid="no-contracts">
        <ScrollText className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
        Aucun contrat pour le moment.
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="candidate-contracts">
      <h2 className="font-display text-2xl font-semibold">Contrats obtenus</h2>
      {contracts.map((c) => (
        <div key={c.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`contract-${c.id}`}>
          <div className="flex items-start justify-between gap-4 mb-2">
            <div>
              <h3 className="font-display text-lg font-semibold">{c.title}</h3>
              <p className="text-sm text-muted-foreground">{[c.client, c.job_title].filter(Boolean).join(" • ")}</p>
            </div>
            <span className="rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-semibold">{LABELS[c.status] || c.status}</span>
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mt-2">
            {c.amount && <span>💰 {c.amount}</span>}
            {c.start_date && <span>Du {c.start_date}</span>}
            {c.end_date && <span>au {c.end_date}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function ProfileForm({ profile, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const cvRef = useRef(null);
  const [cvUploading, setCvUploading] = useState(false);
  const photoRef = useRef(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [delAcc, setDelAcc] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const speedRef = useRef(null);
  const [speedUploading, setSpeedUploading] = useState(false);
  const configRef = useRef(null);
  const [configUploading, setConfigUploading] = useState(false);

  const uploadSpeed = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setSpeedUploading(true);
    try {
      const fd = new FormData();
      fd.append("speedtest", file);
      await api.post("/profile/speedtest", fd);
      toast.success("Speed Test ajouté");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec de l'envoi");
    } finally {
      setSpeedUploading(false);
    }
  };

  const uploadConfig = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setConfigUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("configs", f));
      await api.post("/profile/config", fd);
      toast.success("Configuration ajoutée");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec de l'envoi");
    } finally {
      setConfigUploading(false);
    }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/account");
      toast.success("Votre compte a été supprimé.");
      logout();
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Échec de la suppression du compte");
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (profile) {
      setForm({
        name: profile.name || "",
        phone: profile.phone || "",
        whatsapp: profile.whatsapp || "",
        nationality: profile.nationality || "",
        city: profile.city || "",
        country: profile.country || "",
        current_position: profile.current_position || "",
        years_experience: profile.years_experience ?? "",
        headline: profile.headline || "",
        bio: profile.bio || "",
        domains: (profile.domains || []).join(", "),
        tools: (profile.tools || []).join(", "),
      });
    }
  }, [profile]);

  if (!form) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/profile", {
        name: form.name,
        phone: form.phone,
        whatsapp: form.whatsapp,
        nationality: form.nationality,
        city: form.city,
        country: form.country,
        current_position: form.current_position,
        years_experience: form.years_experience === "" ? null : Number(form.years_experience),
        headline: form.headline,
        bio: form.bio,
        domains: form.domains.split(",").map((s) => s.trim()).filter(Boolean),
        tools: form.tools.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Profil enregistré. L'IA affinera vos recommandations d'offres.");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const field = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

  const uploadCv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setCvUploading(true);
    try {
      const fd = new FormData();
      fd.append("cv", file);
      await api.post("/profile/cv", fd);
      toast.success("CV mis à jour");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec de l'envoi du CV");
    } finally {
      setCvUploading(false);
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      await api.post("/profile/photo", fd);
      toast.success("Photo mise à jour");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec de l'envoi de la photo");
    } finally {
      setPhotoUploading(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-3xl space-y-6" data-testid="profile-form">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold">Mon profil</h2>
          <p className="text-sm text-muted-foreground mt-1">Ces informations aident notre IA à vous proposer les offres les plus pertinentes.</p>
        </div>
        {profile.profile_completed ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-green-500/10 text-green-600 px-3 py-1 text-xs font-semibold" data-testid="profile-status-complete"><CheckCircle2 className="h-3.5 w-3.5" /> Profil complété</span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 text-amber-600 px-3 py-1 text-xs font-semibold" data-testid="profile-status-incomplete"><Clock className="h-3.5 w-3.5" /> À compléter</span>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-gradient-to-br from-primary/5 to-transparent p-6 flex items-center gap-5 flex-wrap" data-testid="photo-card">
        <button type="button" onClick={() => profile.picture && setPhotoOpen(true)} data-testid="profile-photo-preview" className={profile.picture ? "cursor-zoom-in" : "cursor-default"}>
          <Avatar name={form.name || profile.name} src={profile.picture} size={88} />
        </button>
        <div className="flex-1 min-w-[180px]">
          <p className="font-display text-lg font-semibold">{form.name || profile.name || "Votre nom"}</p>
          <p className="text-sm text-muted-foreground">{form.current_position || "Ajoutez votre poste actuel"}</p>
          <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="photo-file-input" />
          <Button type="button" variant="outline" size="sm" className="rounded-full mt-3" disabled={photoUploading} onClick={() => photoRef.current?.click()} data-testid="upload-photo-btn">
            {photoUploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Camera className="h-4 w-4 mr-2" />}
            {profile.picture ? "Modifier la photo" : "Ajouter une photo"}
          </Button>
        </div>
      </div>
      <ImageLightbox src={profile.picture} alt={profile.name} open={photoOpen} onClose={() => setPhotoOpen(false)} />

      <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
        <div className="flex items-center gap-2.5"><span className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0"><User className="h-4 w-4" /></span><p className="font-medium">Informations personnelles</p></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Nom complet *</Label><Input className="mt-1.5" data-testid="profile-name" {...field("name")} required /></div>
          <div><Label>Téléphone *</Label><Input className="mt-1.5" data-testid="profile-phone" {...field("phone")} required /></div>
          <div><Label>Nationalité *</Label><CountrySelect mode="nationality" value={form.nationality} onChange={(v) => setForm({ ...form, nationality: v })} testId="profile-nationality" placeholder="Sélectionnez une nationalité" /></div>
          <div><Label>Ville</Label><Input className="mt-1.5" data-testid="profile-city" {...field("city")} /></div>
          <div><Label>Pays de résidence</Label><CountrySelect mode="country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} testId="profile-country" placeholder="Sélectionnez un pays" /></div>
          <div><Label>Années d'expérience</Label><Input type="number" min="0" className="mt-1.5" data-testid="profile-years" {...field("years_experience")} /></div>
          <div className="sm:col-span-2"><Label>Numéro WhatsApp</Label><WhatsappInput value={form.whatsapp} onChange={(v) => setForm({ ...form, whatsapp: v })} testId="profile-whatsapp" /><p className="text-xs text-muted-foreground mt-1.5">Choisissez l'indicatif du pays puis saisissez votre numéro — ex : +33 6 12 34 56 78.</p></div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 space-y-3" data-testid="cv-card">
        <p className="font-medium">CV / Curriculum Vitae</p>
        {profile.cv_file_id ? (
          <a href={fileUrl(profile.cv_file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-primary hover:underline" data-testid="current-cv-link">
            <FileText className="h-4 w-4" /> {profile.cv_filename || "Voir mon CV"}
          </a>
        ) : (
          <p className="text-sm text-amber-600" data-testid="cv-required-hint">CV requis pour postuler — ajoutez-le ci-dessous (clé pour le matching IA).</p>
        )}
        <input ref={cvRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={uploadCv} data-testid="cv-file-input" />
        <div>
          <Button type="button" variant="outline" className="rounded-full" disabled={cvUploading} onClick={() => cvRef.current?.click()} data-testid="upload-cv-btn">
            {cvUploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            {profile.cv_file_id ? "Modifier mon CV" : "Ajouter mon CV"}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 space-y-4" data-testid="tech-docs-card">
        <p className="font-medium">Documents techniques</p>
        <p className="text-xs text-muted-foreground">Requis pour postuler : le Speed Test de votre connexion et des captures de la configuration de votre ordinateur.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="rounded-xl border border-dashed border-border p-4">
            <p className="text-sm font-medium mb-2 flex items-center gap-2"><Gauge className="h-4 w-4 text-primary" /> Speed Test Internet</p>
            {profile.speedtest_file_id ? (
              <a href={fileUrl(profile.speedtest_file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-xs text-primary hover:underline mb-2" data-testid="current-speedtest-link"><FileText className="h-3.5 w-3.5" /> {profile.speedtest_filename || "Voir le Speed Test"}</a>
            ) : (
              <p className="text-xs text-amber-600 mb-2" data-testid="speedtest-required-hint">Speed Test requis — ajoutez-le ci-dessous.</p>
            )}
            <input ref={speedRef} type="file" accept="image/*,.pdf" className="hidden" onChange={uploadSpeed} data-testid="speedtest-file-input" />
            <Button type="button" variant="outline" size="sm" className="rounded-full" disabled={speedUploading} onClick={() => speedRef.current?.click()} data-testid="upload-speedtest-btn">
              {speedUploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              {profile.speedtest_file_id ? "Modifier" : "Ajouter"}
            </Button>
          </div>
          <div className="rounded-xl border border-dashed border-border p-4">
            <p className="text-sm font-medium mb-2 flex items-center gap-2"><Monitor className="h-4 w-4 text-primary" /> Configuration de l'ordinateur</p>
            {(profile.config_file_ids || []).length > 0 ? (
              <p className="text-xs text-muted-foreground mb-2" data-testid="config-count">{(profile.config_file_ids || []).length} fichier(s) fourni(s)</p>
            ) : (
              <p className="text-xs text-amber-600 mb-2" data-testid="config-required-hint">Captures requises — ajoutez-les ci-dessous (plusieurs possibles).</p>
            )}
            <input ref={configRef} type="file" accept="image/*,.pdf" multiple className="hidden" onChange={uploadConfig} data-testid="config-file-input" />
            <Button type="button" variant="outline" size="sm" className="rounded-full" disabled={configUploading} onClick={() => configRef.current?.click()} data-testid="upload-config-btn">
              {configUploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />} Ajouter des captures
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
        <div className="flex items-center gap-2.5"><span className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0"><Sparkles className="h-4 w-4" /></span><p className="font-medium">Expertise</p></div>
        <div><Label>Poste actuel</Label><Input className="mt-1.5" data-testid="profile-position" placeholder="Ex : Développeur Full-Stack" {...field("current_position")} /></div>
        <div><Label>Domaines d'expertise * <span className="text-muted-foreground font-normal">(séparés par des virgules)</span></Label><Input className="mt-1.5" data-testid="profile-domains" placeholder="Ex : Tech, Data" {...field("domains")} required /></div>
        <div><Label>Outils maîtrisés <span className="text-muted-foreground font-normal">(séparés par des virgules)</span></Label><Input className="mt-1.5" data-testid="profile-tools" placeholder="Ex : React, Python, Figma" {...field("tools")} /></div>
        <div><Label>Titre / accroche</Label><Input className="mt-1.5" data-testid="profile-headline" placeholder="Ex : Ingénieur logiciel passionné" {...field("headline")} /></div>
        <div><Label>À propos</Label><Textarea rows={4} className="mt-1.5" data-testid="profile-bio" placeholder="Parlez de votre parcours..." {...field("bio")} /></div>
      </div>

      {profile.ai_domains?.length > 0 && (
        <div className="rounded-xl bg-primary/5 border border-primary/20 p-4 flex items-center gap-2 text-sm" data-testid="ai-domains">
          <Sparkles className="h-4 w-4 text-primary" />
          <span>Domaines détectés par l'IA : <b>{profile.ai_domains.join(", ")}</b></span>
        </div>
      )}

      <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6" data-testid="danger-zone">
        <p className="font-medium text-destructive mb-1">Zone de danger</p>
        <p className="text-sm text-muted-foreground mb-4">La suppression de votre compte est définitive : votre profil, votre CV, vos candidatures et vos messages seront supprimés et irrécupérables.</p>
        <Button type="button" variant="outline" className="rounded-full border-destructive/40 text-destructive hover:bg-destructive/10" onClick={() => setDelAcc(true)} data-testid="delete-account-btn">
          <Trash2 className="h-4 w-4 mr-2" /> Supprimer mon compte
        </Button>
      </div>

      <div className="sticky bottom-4 flex justify-end">
        <Button type="submit" disabled={saving} className="rounded-full h-12 px-8 shadow-lg shadow-primary/20" data-testid="save-profile-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Enregistrer mon profil
        </Button>
      </div>

      <AlertDialog open={delAcc} onOpenChange={setDelAcc}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer définitivement votre compte ?</AlertDialogTitle>
            <AlertDialogDescription>Cette action est irréversible. Votre profil, CV, candidatures et messages seront supprimés définitivement.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={deleteAccount} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-account">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Oui, supprimer mon compte
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  );
}
